import React from 'react';
import {
  Grid,
  Box,
  makeStyles,
  useTheme,
  useMediaQuery,
} from '@material-ui/core';

import { AssuranceIcon } from 'images';
import Button from 'components/Button';
import { uppercase } from 'utils/string';
import { sanlamDarkBlue, sanlamYellow } from 'Material-UI/colors';
import {
  ReversedStyledGrid,
  StyledCard,
  StyledIcon,
  StyledShortBody,
  StyledCaption,
  StyledGrid,
  StyledCaptionValue,
  StyledResGrid,
} from 'components/ItemInfoCard/styles';
import { EpargneInfoCardProps, styles } from 'components/ItemInfoCard/types';
import { pushTo } from 'helpers/routes';
import { CONTRACT_STATUSES } from 'utils/globalConst';
import StatusChip from 'components/StatusChip';
import EpargneContractBackground from 'images/BG-contract-Epargne.svg';
import messages from 'components/ItemInfoCard/messages';
import { translate } from 'helpers/intl';
import { injectIntl } from 'react-intl';
import PAGES from 'routes/pages';
import { getLocaleSelector } from 'store/LanguageProvider/selectors';
import { useSelector } from 'react-redux';
import { LANGUAGES } from 'store/LanguageProvider/constants';
import GATracking, { EVENT_ACTION, EVENT_CATEGORIES } from 'helpers/GATracking';

const useStyles = makeStyles({
  root: {
    fontSize: '11px !important',
    width: '98px !important',
    borderRadius: '6px',
    color: '#172C51 !important',
    border: '1px solid #FFFFFF !important',
    '&:hover': {
      color: '#285BC7 !important',
    },
  },
});

const EpargneInfoCard = (props: EpargneInfoCardProps) => {
  const {
    dateValue,
    numValue,
    title,
    status,
    wrapperPaddingLeft,
    nameAssure,
    wrapperPaddingRight,
  } = props;
  const classes = useStyles();
  const styles: styles = {
    icon: 'EPARGNEMainIcon',
    titleColor: sanlamYellow,
    labelsColor: 'rgba(255,255,255,0.87)',
    valuesColor: '#FFF',
    backgroundImage: EpargneContractBackground,
    buttonRootStyles: classes.root,
  };
  const lang = useSelector(getLocaleSelector);
  const reverse = lang === LANGUAGES.AR;
  const theme = useTheme();
  const matches = useMediaQuery(theme.breakpoints.down('xs'));
  return (
    <StyledCard
      backgroundImage={styles.backgroundImage}
      backgroundColor={styles.backgroundColor}
      paddingLeft={wrapperPaddingLeft}
      paddingRight={wrapperPaddingRight}
    >
      <StyledGrid
        container
        alignItems="center"
        justify="space-between"
        marginBottom="17px"
      >
        <ReversedStyledGrid reverse={reverse} container justify="space-between">
          <Grid>
            <ReversedStyledGrid
              reverse={reverse}
              container
              justify="space-between"
            >
              <Grid>
                {styles.icon && (
                  <StyledGrid textAlign="center" height="30px">
                    <StyledIcon
                      width={matches ? '16px' : '21px'}
                      height={matches ? '15px' : '20px'}
                      src={AssuranceIcon[styles.icon]}
                      alt="item-icon"
                    />
                  </StyledGrid>
                )}
              </Grid>
              <Grid>
                <Box>
                  <StyledShortBody
                    matches={matches}
                    lineHeight="17px"
                    weight="bold"
                    color={styles.titleColor}
                  >
                    {uppercase(title || '')}
                  </StyledShortBody>
                  <StyledCaption
                    weight="400"
                    fontSize={11}
                    color="rgba(255,255,255,0.87)"
                    marginBottom={0}
                  >
                    {uppercase(nameAssure)}
                  </StyledCaption>
                </Box>
              </Grid>
            </ReversedStyledGrid>
          </Grid>
          <StyledGrid item xs={3} textAlign="end">
            {status === CONTRACT_STATUSES.ACTIVE ? (
              <StatusChip
                matches={matches}
                label={translate({ ...messages.Actif }, props)}
                status="success"
              />
            ) : (
              <StatusChip
                matches={matches}
                label={translate({ ...messages.Encours }, props)}
                status="error"
              />
            )}
          </StyledGrid>
        </ReversedStyledGrid>
      </StyledGrid>

      <Grid container justify="space-between">
        <ReversedStyledGrid reverse={reverse} container justify="space-between">
          <Grid>
            <StyledCaption
              matches={matches}
              color={styles.labelsColor || '#9797AF'}
              marginBottom="6px"
            >
              {uppercase(translate({ ...messages.dateEffet }, props))}
            </StyledCaption>
          </Grid>
          <Grid>
            <StyledCaptionValue
              matches={matches}
              color={styles.valuesColor || sanlamDarkBlue}
              weight="bold"
              marginBottom="6px"
              textAlign="end"
            >
              {dateValue}
            </StyledCaptionValue>
          </Grid>
        </ReversedStyledGrid>
        <ReversedStyledGrid reverse={reverse} container justify="space-between">
          <Grid>
            <StyledCaption
              matches={matches}
              color={styles.labelsColor || '#9797AF'}
            >
              {uppercase(translate({ ...messages.NumContract }, props))}
            </StyledCaption>
          </Grid>
          <Grid>
            <StyledCaptionValue
              matches={matches}
              color={styles.valuesColor || sanlamDarkBlue}
              weight="bold"
              textAlign="end"
              marginBottom="15px"
            >
              {numValue}
            </StyledCaptionValue>
          </Grid>
        </ReversedStyledGrid>

        <ReversedStyledGrid reverse={reverse} container justify="space-between">
          <Grid>
            <Button
              variant="outlined"
              className={styles.buttonRootStyles}
              size="small"
              onClick={() => {
                props.setDetailsPageBreadcrumbsPath([
                  { name: 'epargn', route: PAGES.EPARGN_DASHBOARD },
                  { name: 'mesContrats' },
                ]);
                pushTo(PAGES.EPARGN_DETAILS);
                GATracking.event(
                  EVENT_CATEGORIES.EPARGN_ACCUEIL,
                  EVENT_ACTION.CLICK,
                  "Mes contrats d'épargne_Voir détails",
                );
              }}
            >
              <StyledResGrid matches={matches}>
                {translate({ ...messages.voirTous }, props)}
              </StyledResGrid>
            </Button>
          </Grid>
        </ReversedStyledGrid>
      </Grid>
    </StyledCard>
  );
};

export default injectIntl(EpargneInfoCard);
